import { useMemo } from 'react';

import { useLaunchDarklyFlag } from '@/hooks/flags/useLaunchDarklyFlag';
import { isPrepaymentRequired } from '@/utils/bookingCartUtils';
import { PaymentMethodType } from '@/utils/paymentMethods';

import type { UseShouldShowPrepaymentNoticeParams } from './types';

/**
 * Determines if the prepayment disclaimer banner should be shown.
 *
 * Nothing is shown when the cart has no outstanding amount (e.g. fully covered
 * by a gift card), since no charge happens today.
 *
 * The LD flag must be enabled, then at least ONE condition must be true:
 * 1. Shop requires prepayment
 * 2. Shop requires partial deposits
 * 3. At least one cart item requires prepayment
 * 4. At least one cart item requires a deposit
 * 5. Customer is choosing to prepay (selected a non-payInPerson payment method)
 */
export const useShouldShowPrepaymentNotice = ({
  shop,
  cart,
  selectedPaymentMethod,
}: UseShouldShowPrepaymentNoticeParams): boolean => {
  const ldFlagEnabled = useLaunchDarklyFlag('payment-deposits-enabled', false);

  return useMemo(() => {
    // shop.settings.paymentDepositsEnabled is intentionally not checked here —
    // water-on-web uses LD as the sole feature gate, with no per-shop setting.
    if (!ldFlagEnabled || !shop) {
      return false;
    }

    if (cart?.breakdown?.outstandingChargeAmount === 0) {
      return false;
    }

    const isCustomerChoosingToPrepay =
      !!selectedPaymentMethod &&
      selectedPaymentMethod.type !== PaymentMethodType.payInPerson;

    // Shop/cart conditions show the banner before a method is chosen (or when pay-in-person
    // is selected); isCustomerChoosingToPrepay covers the case where a prepay method is picked.
    return (
      isPrepaymentRequired(shop, cart?.items) || isCustomerChoosingToPrepay
    );
  }, [ldFlagEnabled, shop, cart, selectedPaymentMethod]);
};
